import { PersistentStorage } from '/fp/persistent-storage.js';
import { readGlobalMarkerCached } from '/fp/global-marker.js';
import { hmacSha256Sign } from '/fp/hmac-sha256.js';
import { computePrewarmTimings } from '/fp/prewarm/prewarm-timings.mjs';
import config from '/config.js';

function perfNow() {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
}

const MODULE_LOAD_TS = perfNow();
export const LOCAL_FALLBACK = { markers: {}, details: {} };

let _started = false;
let _prewarmStartTs = null;
let _localPromise = null;
let _localSettled = false;
let _globalPromise = null;
let _globalSettled = false;

async function generateLocalToken() {
  const signingKey = config._k;
  const randomBytes = crypto.getRandomValues(new Uint8Array(16));
  const keyBytes = new TextEncoder().encode(signingKey);

  let signature;
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    signature = await crypto.subtle.sign('HMAC', key, randomBytes);
  } else {
    signature = hmacSha256Sign(keyBytes, randomBytes);
  }

  const payload = btoa(String.fromCharCode(...randomBytes));
  const sig = btoa(String.fromCharCode(...new Uint8Array(signature)));
  return `${payload}.${sig}`;
}

function scheduleMarkerStart(fn) {
  setTimeout(fn, 0);
}

function waitForBody() {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(false);
      return;
    }
    if (document.body) {
      resolve(true);
      return;
    }
    if (document.readyState !== 'loading') {
      resolve(!!document.body);
      return;
    }
    const onReady = () => {
      document.removeEventListener('DOMContentLoaded', onReady);
      resolve(!!document.body);
    };
    document.addEventListener('DOMContentLoaded', onReady);
  });
}

function ensureStarted() {
  if (_started) return;
  if (typeof window === 'undefined') return;
  _started = true;
  _prewarmStartTs = perfNow();

  _localPromise = new Promise((resolve) => {
    scheduleMarkerStart(() => {
      try {
        const storage = new PersistentStorage('sdk');
        resolve(storage.store('__bb__', () => generateLocalToken()));
      } catch (e) {
        resolve(LOCAL_FALLBACK);
      }
    });
  }).then(
    (res) => {
      _localSettled = true;
      return res;
    },
    () => {
      _localSettled = true;
      return LOCAL_FALLBACK;
    }
  );

  if (__GLOBAL_MARKER__) {
    _globalPromise = waitForBody()
      .then((ok) => (ok ? readGlobalMarkerCached(config.defaultEndpoint) : null))
      .catch(() => null);
    _globalPromise.then(
      () => {
        _globalSettled = true;
      },
      () => {
        _globalSettled = true;
      }
    );
  } else {
    _globalPromise = Promise.resolve(null);
  }
}

export function startPrewarm() {
  ensureStarted();
}

export function getLocalMarker() {
  ensureStarted();
  return _localPromise || Promise.resolve(LOCAL_FALLBACK);
}

export function getGlobalMarker() {
  ensureStarted();
  return _globalPromise || Promise.resolve(null);
}

export function getPrewarmTimings(collectTs) {
  const t = typeof collectTs === 'number' ? collectTs : perfNow();
  return computePrewarmTimings({
    collectTs: t,
    prewarmStartTs: _prewarmStartTs,
    moduleLoadTs: MODULE_LOAD_TS,
    localSettled: _localSettled,
    globalSettled: _globalSettled,
  });
}
